import React, { useState, useEffect } from 'react';
import { DocumentTemplate, DocumentTrackingItem } from '../../types';

interface DocumentsTrackingProps {
  clientId: string;
  onDocumentSelect?: (documentId: string) => void;
}

const DocumentsTracking: React.FC<DocumentsTrackingProps> = ({
  clientId,
  onDocumentSelect
}) => {
  const [completedDocuments, setCompletedDocuments] = useState<DocumentTrackingItem[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<DocumentTrackingItem[]>([]);

  // Completed documents (populated from real data when available)
  const mockCompletedDocuments: DocumentTrackingItem[] = [];

  useEffect(() => {
    // In a real app, this would fetch from API
    setCompletedDocuments(mockCompletedDocuments);
    setPendingDocuments([]); // No pending documents for now
  }, [clientId]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const handleDocumentClick = (documentId: string) => {
    if (onDocumentSelect) {
      onDocumentSelect(documentId);
    }
  };

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#f9fafb',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            color: '#374151',
            fontSize: '2rem',
            margin: '0 0 0.5rem 0',
            fontWeight: '600'
          }}>
            Documents, Forms and Files
          </h1>
        </div>

        {/* Pending Documents Section */}
        {pendingDocuments.length > 0 ? (
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{
              color: '#374151',
              fontSize: '1.5rem',
              marginBottom: '1rem',
              fontWeight: '600'
            }}>
              Pending
            </h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {pendingDocuments.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => handleDocumentClick(doc.id)}
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{
                      fontSize: '1.25rem',
                      marginRight: '1rem',
                      color: '#f59e0b'
                    }}>
                      {doc.icon}
                    </span>
                    <span style={{
                      color: '#374151',
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}>
                      {doc.title}
                    </span>
                  </div>
                  <span style={{
                    color: '#f59e0b',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}>
                    Pending
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '3rem 1rem',
            color: '#d1d5db',
            fontSize: '1rem'
          }}>
            No pending documents at this time.
          </div>
        )}

        {/* Completed Documents Section */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{
              color: '#374151',
              fontSize: '1.5rem',
              margin: 0,
              fontWeight: '600'
            }}>
              Completed
            </h2>
            <span style={{
              color: '#d1d5db',
              fontSize: '0.875rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              DATE COMPLETED
            </span>
          </div>

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {completedDocuments.map((doc) => (
              <div
                key={doc.id}
                onClick={() => handleDocumentClick(doc.id)}
                style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1rem 1.25rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '1.25rem',
                    marginRight: '1rem',
                    color: '#10b981'
                  }}>
                    {doc.icon}
                  </span>
                  <span style={{
                    color: '#10b981',
                    fontSize: '1rem',
                    fontWeight: '500'
                  }}>
                    {doc.title}
                  </span>
                </div>
                <span style={{
                  color: '#d1d5db',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {doc.completedDate ? formatDate(doc.completedDate) : ''}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: '#f3f4f6',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.875rem',
            color: '#d1d5db'
          }}>
            <span>
              <strong style={{ color: '#10b981' }}>{completedDocuments.length}</strong> completed documents
            </span>
            <span>
              <strong style={{ color: '#f59e0b' }}>{pendingDocuments.length}</strong> pending documents
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsTracking;